import React, { useMemo } from "react";
import type { ServiceCenterPostResponseDto } from "@pro-sto/api-client";
import { AppSwitch } from "@pro-sto/ui";
import "./PostsHeader.css";

interface PostsHeaderProps {
  posts: ServiceCenterPostResponseDto[];
  onAddPost: () => void;
  onTogglePostAvailability?: (postUuid: string, isAvailable: boolean) => void;
  isLoading?: boolean;
  postColumnWidth?: number;
}

const PlusIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 4V16M4 10H16"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const PostsHeader: React.FC<PostsHeaderProps> = ({
  posts,
  onAddPost,
  onTogglePostAvailability,
  isLoading = false,
  postColumnWidth,
}) => {
  // Сортируем посты по orderNumber
  const sortedPosts = useMemo(() => {
    return [...posts].sort((a, b) => a.orderNumber - b.orderNumber);
  }, [posts]);

  const handleToggleAvailability = (postUuid: string, isAvailable: boolean) => {
    onTogglePostAvailability?.(postUuid, isAvailable);
  };

  if (isLoading) {
    return (
      <div className="posts-header">
        <div className="posts-header__time-label">Время</div>
        <div className="posts-header__posts">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="posts-header__post posts-header__post--skeleton"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="posts-header" data-testid="posts-header">
        {/* Обёртка прилипает к левому краю при горизонтальной прокрутке колонок,
            чтобы кнопка добавления поста оставалась доступной на любом посте.
            Непрозрачный фон нужен, чтобы колонки не просвечивали под кнопкой. */}
        <div className="posts-header__add-post-sticky">
          <button
            className="posts-header__add-post"
            onClick={onAddPost}
            aria-label="Добавить пост"
            type="button"
            data-testid="posts-header-add-button"
          >
            <PlusIcon />
          </button>
        </div>

        <div className="posts-header__posts">
          <div className="posts-header__posts-wrapper">
            {sortedPosts.map((post, index) => (
              <div
                key={post.uuid}
                className="posts-header__post"
                style={
                  postColumnWidth
                    ? { width: `${postColumnWidth}px`, flex: "0 0 auto" }
                    : undefined
                }
                data-testid={`post-label-${index}`}
              >
                <div className="posts-header__post-row">
                  <div className="posts-header__post-content">
                    <span className="posts-header__post-label">пост</span>
                    <span className="posts-header__post-number">
                      {post.orderNumber}
                    </span>
                  </div>
                  <AppSwitch
                    checked={post.isAvailableForOnlineBooking}
                    onChange={(checked) =>
                      handleToggleAvailability(post.uuid, checked)
                    }
                    size="M"
                    data-testid={`post-switch-${index}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default PostsHeader;
